import React, { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import logger from "../utils/logger";
import { Search, AlertTriangle, Building2, User, Gavel, Newspaper, Shield } from "lucide-react";
import IdentityResultCard from "../components/IdentityResultCard";
import { fromEntityIntel } from "../lib/identityNormalizers";

const API = `${window.location.origin}/api`;

const NODE_COLORS = {
  person: "#2563eb",
  company: "#10b981",
  court_case: "#f59e0b",
  sanction: "#dc2626",
  enforcement: "#8B0000",
};

const RISK_COLORS = {
  low: "#10b981", medium: "#f59e0b", high: "#ef4444", critical: "#dc2626",
};

function ForceGraph({ nodes, links }) {
  const canvasRef = useRef(null);
  const [hoveredNode, setHoveredNode] = useState(null);

  useEffect(() => {
    if (!canvasRef.current || !nodes.length) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const W = canvas.width = canvas.parentElement.clientWidth;
    const H = canvas.height = 480;

    // Force layout — spread nodes wider to avoid text overlap
    const cx = W / 2;
    const cy = H / 2;
    const positioned = nodes.map((n, i) => {
      if (i === 0) return { ...n, x: cx, y: cy };
      const angle = (2 * Math.PI * (i - 1)) / (nodes.length - 1) - Math.PI / 2;
      const radius = 170 + (i % 2) * 50;
      return { ...n, x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
    });

    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      // Draw links
      links.forEach(link => {
        const src = positioned.find(n => n.id === link.source);
        const tgt = positioned.find(n => n.id === link.target);
        if (!src || !tgt) return;
        ctx.beginPath();
        ctx.moveTo(src.x, src.y);
        ctx.lineTo(tgt.x, tgt.y);
        ctx.strokeStyle = "#1e2530";
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Label on link
        const mx = (src.x + tgt.x) / 2;
        const my = (src.y + tgt.y) / 2;
        ctx.font = "9px DM Sans, sans-serif";
        ctx.fillStyle = "#475569";
        ctx.textAlign = "center";
        ctx.fillText(link.relationship, mx, my - 4);
      });

      // Draw nodes
      positioned.forEach(n => {
        const r = n.id === nodes[0]?.id ? 24 : 18;
        const color = NODE_COLORS[n.type] || "#475569";
        const riskColor = RISK_COLORS[n.risk] || "#475569";

        // Glow
        ctx.beginPath();
        ctx.arc(n.x, n.y, r + 4, 0, 2 * Math.PI);
        ctx.fillStyle = `${riskColor}22`;
        ctx.fill();

        // Circle
        ctx.beginPath();
        ctx.arc(n.x, n.y, r, 0, 2 * Math.PI);
        ctx.fillStyle = `${color}33`;
        ctx.fill();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.stroke();

        // Icon letter
        ctx.font = `bold ${r > 20 ? 12 : 10}px DM Sans, sans-serif`;
        ctx.fillStyle = color;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const icon = n.type === "person" ? "P" : n.type === "company" ? "C" : n.type === "court_case" ? "J" : n.type === "sanction" ? "S" : "E";
        ctx.fillText(icon, n.x, n.y);

        // Name below
        ctx.font = "10px DM Sans, sans-serif";
        ctx.fillStyle = "#f1f5f9";
        ctx.textAlign = "center";
        const name = n.name.length > 22 ? n.name.slice(0, 20) + "..." : n.name;
        ctx.fillText(name, n.x, n.y + r + 14);

        // Risk badge
        ctx.font = "bold 8px DM Sans, sans-serif";
        ctx.fillStyle = riskColor;
        ctx.fillText(n.risk.toUpperCase(), n.x, n.y + r + 26);
      });
    };

    draw();
  }, [nodes, links]);

  return <canvas ref={canvasRef} style={{ width: "100%", height: "480px", borderRadius: "8px" }} />;
}

export default function EntityIntelligencePage() {
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/entity-intelligence/search?q=${encodeURIComponent(query)}`, { withCredentials: true });
      setSearchResults(data.results || []);
      if (data.results?.length === 1) {
        loadEntity(data.results[0].key);
      }
    } catch (err) {
      logger.error("Entity search failed:", err);
    } finally {
      setLoading(false);
    }
  }, [query]);

  const loadEntity = async (key) => {
    try {
      const { data } = await axios.get(`${API}/entity-intelligence/${key}`, { withCredentials: true });
      setEntity(data);
      setSearchResults([]);
    } catch (err) {
      logger.error("Entity load failed:", err);
    }
  };

  return (
    <div data-testid="entity-intelligence-page">
      <div className="mb-6">
        <h1 style={{ fontSize: "26px", fontWeight: 700, letterSpacing: "-0.5px", color: "#f1f5f9", marginBottom: "4px" }} data-testid="entity-intelligence-title">
          Entity Intelligence
        </h1>
        <p style={{ color: "#94a3b8", fontSize: "14px" }}>Visualize entity relationships, risk connections, and due diligence insights</p>
      </div>

      {/* Search */}
      <div className="card-aml mb-5" style={{ padding: "12px" }}>
        <div className="flex items-center gap-2">
          <Search style={{ width: "16px", height: "16px", color: "#475569" }} />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
            placeholder="Search entity: Rajesh Sharma, Ananya Textiles, Deepak Malhotra, Vikram Singh..."
            data-testid="entity-search-input"
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              color: "#f1f5f9", fontSize: "14px", padding: "8px",
            }}
          />
          <button onClick={handleSearch} disabled={loading} data-testid="entity-search-btn" style={{
            background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none",
            borderRadius: "8px", padding: "8px 20px", cursor: "pointer",
            color: "#fff", fontSize: "13px", fontWeight: 600,
          }}>
            {loading ? "Searching..." : "Search"}
          </button>
        </div>

        {/* Search Results Dropdown */}
        {searchResults.length > 1 && (
          <div style={{ marginTop: "8px", borderTop: "1px solid #1e2530", paddingTop: "8px" }}>
            {searchResults.map(r => (
              <button key={r.key} onClick={() => loadEntity(r.key)} style={{
                display: "flex", alignItems: "center", gap: "8px", width: "100%",
                padding: "8px 12px", background: "transparent", border: "none",
                cursor: "pointer", color: "#f1f5f9", fontSize: "13px", textAlign: "left",
                borderRadius: "6px",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#0f1520"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
              >
                {r.type === "individual" ? <User style={{ width: 14, height: 14, color: "#2563eb" }} /> : <Building2 style={{ width: 14, height: 14, color: "#10b981" }} />}
                <span>{r.name}</span>
                <span style={{ marginLeft: "auto", fontSize: "11px", fontWeight: 700, color: RISK_COLORS[r.risk_score > 70 ? "high" : r.risk_score > 40 ? "medium" : "low"] }}>
                  Score: {r.risk_score}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Entity Graph */}
      {entity && (
        <div>
          {/* Unified identity card */}
          <div style={{ marginBottom: 20 }}>
            <IdentityResultCard person={fromEntityIntel(entity, entity.name)} />
          </div>
          {/* Entity Header */}
          <div className="card-aml mb-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div style={{
                  width: "42px", height: "42px", borderRadius: "10px",
                  background: entity.type === "individual" ? "rgba(37,99,235,0.12)" : "rgba(16,185,129,0.12)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  {entity.type === "individual"
                    ? <User style={{ width: "20px", height: "20px", color: "#2563eb" }} />
                    : <Building2 style={{ width: "20px", height: "20px", color: "#10b981" }} />
                  }
                </div>
                <div>
                  <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#f1f5f9" }}>{entity.name}</h2>
                  <div className="flex items-center gap-3" style={{ fontSize: "12px", color: "#475569" }}>
                    <span>{entity.type === "individual" ? "Individual" : "Corporate"}</span>
                    {entity.pan && <span>PAN: {entity.pan}</span>}
                    {entity.cin && <span>CIN: {entity.cin}</span>}
                  </div>
                </div>
              </div>
              <div style={{
                fontSize: "24px", fontWeight: 700,
                color: entity.risk_score > 80 ? "#dc2626" : entity.risk_score > 60 ? "#ef4444" : entity.risk_score > 40 ? "#f59e0b" : "#10b981",
              }}>
                {entity.risk_score}
                <span style={{ fontSize: "10px", color: "#475569", marginLeft: "4px" }}>/ 100</span>
              </div>
            </div>

            {/* Critical Risk Badge */}
            {entity.risk_score > 80 && (
              <div data-testid="critical-risk-badge" style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "6px 14px", borderRadius: "6px", marginBottom: "14px",
                background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.3)",
              }}>
                <AlertTriangle style={{ width: "14px", height: "14px", color: "#dc2626" }} />
                <span style={{ fontSize: "12px", fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Critical Risk
                </span>
              </div>
            )}

            {/* Graph Canvas */}
            <div data-testid="entity-graph" style={{ background: "#080c12", borderRadius: "10px", padding: "12px", border: "1px solid #1e2530" }}>
              <ForceGraph nodes={entity.nodes} links={entity.links} />
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-5 mt-3">
              {[
                { type: "person", label: "Person", color: "#2563eb" },
                { type: "company", label: "Company", color: "#10b981" },
                { type: "court_case", label: "Court Case", color: "#f59e0b" },
                { type: "sanction", label: "Sanction/List", color: "#dc2626" },
              ].map(l => (
                <div key={l.type} className="flex items-center gap-1.5">
                  <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: l.color }} />
                  <span style={{ fontSize: "10px", color: "#94a3b8" }}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Risk Summary Box */}
          {entity.risk_summary && (
            <div data-testid="risk-summary-box" className="card-aml mb-5" style={{
              borderLeft: "4px solid #dc2626",
              background: "rgba(220,38,38,0.04)",
            }}>
              <div className="flex items-center gap-2 mb-2">
                <Shield style={{ width: "14px", height: "14px", color: "#dc2626" }} />
                <span style={{ fontSize: "12px", fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Risk Assessment
                </span>
              </div>
              <p style={{ fontSize: "13px", color: "#f1f5f9", lineHeight: 1.6, margin: 0 }}>
                {entity.risk_summary}
              </p>
            </div>
          )}

          {/* News Mentions */}
          {entity.news && entity.news.length > 0 && (
            <div className="card-aml" data-testid="entity-news">
              <div className="flex items-center gap-2 mb-3">
                <Newspaper style={{ width: "14px", height: "14px", color: "#FF6B35" }} />
                <h3 style={{ fontSize: "13px", fontWeight: 700, color: "#f1f5f9" }}>News & Media Mentions</h3>
              </div>
              <div className="space-y-2">
                {entity.news.map((n, idx) => (
                  <div key={n.title} style={{
                    padding: "10px 12px", background: "#080c12", borderRadius: "8px",
                    border: `1px solid ${n.sentiment === "negative" ? "rgba(239,68,68,0.15)" : "#1e2530"}`,
                  }}>
                    <div style={{ fontSize: "12px", fontWeight: 600, color: "#f1f5f9" }}>{n.title}</div>
                    <div className="flex items-center gap-3 mt-1" style={{ fontSize: "10px", color: "#475569" }}>
                      <span>{n.source}</span>
                      <span>{n.date}</span>
                      <span style={{
                        fontWeight: 700, textTransform: "uppercase",
                        color: n.sentiment === "negative" ? "#ef4444" : "#10b981",
                      }}>
                        {n.sentiment}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!entity && searchResults.length === 0 && (
        <div className="card-aml" style={{ padding: "60px", textAlign: "center" }}>
          <Shield style={{ width: "40px", height: "40px", color: "#1e2530", margin: "0 auto 16px" }} />
          <h3 style={{ fontSize: "15px", fontWeight: 600, color: "#475569", marginBottom: "6px" }}>Search for an entity</h3>
          <p style={{ fontSize: "12px", color: "#334155" }}>
            Try searching: <em>Rajesh Sharma</em>, <em>Ananya Textiles</em>, <em>Deepak Malhotra</em>, or <em>Vikram Singh</em>
          </p>
        </div>
      )}
      {/* Bottom Branding */}
      <div style={{ textAlign: "center", marginTop: "32px", padding: "16px 0", borderTop: "1px solid #1e2530" }}>
        <div style={{ fontSize: "10px", color: "#334155" }}>
          &copy; 2026 Rudrik Technologies Private Limited
        </div>
      </div>
    </div>
  );
}
